// ═══════════════════════════════════════════════════════════════
// PeoplePicker — Searchable dropdown backed by the people table
// Drop this anywhere you need to select a person from the directory.
// Loads people once on mount, filters client-side after.
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { Select } from '../gab/GabComponents';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

export interface PickedPerson {
  record_id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  title: string;
  department: string;
  type: string;
}

interface PeoplePickerProps {
  label?: string;
  placeholder?: string;
  /** Currently selected person record_id as string, or null */
  value: string | null;
  /** Called with (record_id string | null, full person object | null) */
  onChange: (id: string | null, person: PickedPerson | null) => void;
  size?: 'xs' | 'sm' | 'md';
  clearable?: boolean;
  disabled?: boolean;
  required?: boolean;
  /**
   * Restrict to one or more person types. Single value: 'employee'.
   * Multiple values: 'employee,liaison' — runs parallel queries, one per type.
   */
  typeFilter?: string;
  /** Optional extra style on the wrapping div */
  style?: React.CSSProperties;
}

function mapRecord(r: Record<string, unknown>): PickedPerson {
  const fn = String(r[FIELDS.people.firstName] ?? '');
  const ln = String(r[FIELDS.people.lastName] ?? '');
  return {
    record_id: r.record_id as number,
    firstName: fn,
    lastName: ln,
    fullName: [fn, ln].filter(Boolean).join(' '),
    email: String(r[FIELDS.people.email] ?? ''),
    phone: String(r[FIELDS.people.phone] ?? ''),
    title: String(r[FIELDS.people.title] ?? ''),
    department: String(r[FIELDS.people.department] ?? ''),
    type: String(r[FIELDS.people.type] ?? ''),
  };
}

export function PeoplePicker({
  label = 'Person',
  placeholder = 'Search by name or email…',
  value,
  onChange,
  size = 'sm',
  clearable = true,
  disabled,
  required,
  typeFilter,
  style,
}: PeoplePickerProps) {
  const [options, setOptions] = useState<{ value: string; label: string; person: PickedPerson }[]>([]);
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current || !TABLES.people) return;
    loaded.current = true;
    setLoading(true);

    const types = typeFilter ? typeFilter.split(',').map(t => t.trim()).filter(Boolean) : [];

    // Build one query per type (parallel), or a single no-filter query when unrestricted
    const queries = types.length > 0
      ? types.map(t => queryRecords(TABLES.people, {
          filters: [{ field: FIELDS.people.type, operator: 'eq' as const, value: t }],
          sort: { field: FIELDS.people.lastName, direction: 'asc' },
          limit: 500,
        }))
      : [queryRecords(TABLES.people, {
          sort: { field: FIELDS.people.lastName, direction: 'asc' },
          limit: 500,
        })];

    Promise.all(queries)
      .then(results => {
        const allRecords = results.flatMap(r => r.records as Record<string, unknown>[]);
        // De-duplicate by record_id (a person could theoretically match multiple type queries)
        const seen = new Set<number>();
        const unique = allRecords.filter(r => {
          const id = r.record_id as number;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        // Sort merged results by lastName
        unique.sort((a, b) =>
          String(a[FIELDS.people.lastName] ?? '').localeCompare(String(b[FIELDS.people.lastName] ?? ''))
        );

        const opts = unique
          .map(r => {
            const p = mapRecord(r);
            return {
              value: String(r.record_id),
              label: `${p.fullName}${p.email ? ` (${p.email})` : ''}`,
              person: p,
            };
          })
          .filter(o => o.person.fullName.trim().length > 0)
          // Client-side fallback: if server-side filter returned extras, strip them
          .filter(o => types.length === 0 || types.includes(o.person.type));

        setOptions(opts);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (val: string | null) => {
    if (!val) { onChange(null, null); return; }
    const opt = options.find(o => o.value === val);
    onChange(val, opt?.person ?? null);
  };

  return (
    <div style={style}>
      <Select
        label={label}
        placeholder={loading ? 'Loading directory…' : placeholder}
        searchable
        clearable={clearable}
        data={options.map(o => ({ value: o.value, label: o.label }))}
        value={value}
        onChange={handleChange}
        size={size}
        disabled={disabled || loading}
        required={required}
        nothingFoundMessage={loading ? 'Loading…' : 'No matches — try a different name'}
      />
    </div>
  );
}
